// Sticky action bar at the bottom of the builder: Run backtest / Save draft /
// Save + activate. Calls the orchestrator + (optionally) HistoricalIndicators, and
// after a successful create-with-cached-backtest, persists the metrics onto the
// strategy so the first webhook payload has populated score / winRate / suggestedSL.

import { useState } from "react";
import { toast } from "sonner";
import { Play, Save, Rocket, Loader2 } from "lucide-react";
import { useBuilderState, type BuilderDraft } from "../../state/BuilderState";
import { useSelectedStrategy } from "../../state/SelectedStrategyContext";
import { useNavigation } from "../../state/NavigationContext";
import { createStrategy, patchStrategyBacktest, patchStrategyStatus } from "../../api/strategies";
import { runBacktest } from "../../api/backtests";
import { selectPrimary } from "../backtest/primary";
import type { BacktestResponseDto, SaveStrategyRequestDto } from "../../api/types";

interface Props {
  /** Most-recent backtest result from the parent BuilderPage. Persisted onto the new
   *  strategy after Save so the first webhook payload carries rich metrics. */
  lastBacktest: BacktestResponseDto | null;
  onBacktestResult: (r: BacktestResponseDto | null) => void;
}

export function ReviewBar({ lastBacktest, onBacktestResult }: Props) {
  const { draft } = useBuilderState();
  const { setSelectedStrategyId } = useSelectedStrategy();
  const { navigateTo } = useNavigation();
  const [busy, setBusy] = useState<"backtest" | "draft" | "activate" | null>(null);

  const handleBacktest = async () => {
    setBusy("backtest");
    try {
      const result = await runBacktest({
        strategy: toSpec(draft),
        direction: "both",
        minOccurrences: 5,
      });
      onBacktestResult(result);
      const s = result.strategy;
      toast.success("Backtest complete", {
        description: s
          ? `${s.trades} trades (${s.longTrades}L/${s.shortTrades}S, ${s.reversals} rev), total ${s.totalReturn.toFixed(2)}%`
          : `${result.long?.trades ?? 0}L / ${result.short?.trades ?? 0}S trades`,
      });
    } catch (err) {
      toast.error("Backtest failed", { description: (err as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const handleSave = async (activate: boolean) => {
    // Guard against saving an incomplete / no-op strategy. The default draft no longer seeds
    // condition indices, so a strategy with no entry conditions selected can never fire — block
    // it here rather than silently persisting it.
    const name = draft.name.trim();
    if (!draft.coin.trim()) {
      toast.error("Pick a coin", { description: "Choose a coin in the Setup step first." });
      return;
    }
    if (!name) {
      toast.error("Name your strategy", { description: "Enter a display name before saving." });
      return;
    }
    if (draft.longCondIndices.length === 0 && draft.shortCondIndices.length === 0) {
      toast.error("No strategy selected", {
        description: "Run a scan and choose a candidate in the Discover step first.",
      });
      return;
    }

    setBusy(activate ? "activate" : "draft");
    try {
      const req: SaveStrategyRequestDto = {
        name,
        spec: toSpec(draft),
        channels: draft.channels.map((c) => ({ type: c.type, url: c.url, enabled: c.enabled })),
      };
      const created = await createStrategy(req);
      if (lastBacktest) {
        try { await patchStrategyBacktest(created.id, selectPrimary(lastBacktest) ?? lastBacktest); }
        catch { /* non-fatal — strategy still saved without rich metrics */ }
      }
      if (activate) {
        await patchStrategyStatus(created.id, "active");
      }
      setSelectedStrategyId(created.id);
      toast.success(activate ? "Strategy activated" : "Draft saved", {
        description: `${draft.name} · id ${created.id.slice(0, 8)}…`,
      });
      if (activate) {
        navigateTo("monitor");
      }
    } catch (err) {
      toast.error(activate ? "Activate failed" : "Save failed", { description: (err as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const Spinner = <Loader2 className="size-4 animate-spin" />;

  return (
    <div className="sticky bottom-0 z-10 -mx-4 px-4 py-3 bg-background border-t border-border flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={handleBacktest}
        disabled={busy !== null}
        className="flex items-center gap-2 py-2.5 px-4 rounded-xl border-2 border-primary/40 text-primary hover:bg-primary/10 font-bold text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy === "backtest" ? Spinner : <Play className="size-4" />}
        Run backtest
      </button>
      <button
        type="button"
        onClick={() => handleSave(false)}
        disabled={busy !== null}
        className="flex items-center gap-2 py-2.5 px-4 rounded-xl border-2 border-border bg-card-elevated hover:border-primary/50 font-bold text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy === "draft" ? Spinner : <Save className="size-4" />}
        Save draft
      </button>
      <button
        type="button"
        onClick={() => handleSave(true)}
        disabled={busy !== null}
        className="flex items-center gap-2 py-2.5 px-5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy === "activate" ? Spinner : <Rocket className="size-4" />}
        Save + Activate
      </button>
    </div>
  );
}

/** A positive finite number, else null (so empty inputs disable that risk exit). */
function posOrNull(v: number | null | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
}

/** Project BuilderDraft → wire-format StrategySpecDto. */
function toSpec(draft: BuilderDraft) {
  const sl = posOrNull(draft.riskSettings?.stopLossPercent);
  const tp = posOrNull(draft.riskSettings?.takeProfitPercent);
  const tr = posOrNull(draft.riskSettings?.trailingStopPercent);
  // All-unset ⇒ send null (pre-Phase-12 behaviour) rather than an object of nulls.
  const riskSettings = sl === null && tp === null && tr === null
    ? null
    : { stopLossPercent: sl, takeProfitPercent: tp, trailingStopPercent: tr };
  return {
    coin: draft.coin,
    interval: draft.interval,
    marketType: draft.marketType,
    indicatorSpecs: draft.indicatorSpecs,
    longCondIndices: draft.longCondIndices,
    shortCondIndices: draft.shortCondIndices,
    // Phase A: send the exit-condition arrays. Backend accepts them as nullable; we send
    // null when empty so server-side default-binding doesn't conflate "no exits" with
    // "we didn't ship the field yet" (older clients).
    exitLongCondIndices:  draft.exitLongCondIndices.length  > 0 ? draft.exitLongCondIndices  : null,
    exitShortCondIndices: draft.exitShortCondIndices.length > 0 ? draft.exitShortCondIndices : null,
    exitMode: draft.exitMode,
    forwardBars: draft.forwardBars,
    useAdvanced: draft.useAdvanced,
    usePriceAction: draft.usePriceAction,
    riskSettings,
  };
}

// Re-export so MonitorPage can use the same projection without duplicating it.
export { toSpec };
